import { useCallback, useMemo, useState } from 'react';

export const MAX_TOASTS = 3;

// How long each type stays up. null = until dismissed (errors must be read).
export const TOAST_DURATIONS = {
  success: 4000,
  info: 4000,
  warning: 8000,
  error: null,
};

export const toastDuration = (type, override) =>
  override !== undefined ? override : (type in TOAST_DURATIONS ? TOAST_DURATIONS[type] : TOAST_DURATIONS.info);

// Add a toast to a newest-first stack, capped at `max`. A toast with the same message
// and type as one already showing replaces it (moving it to the top with a fresh
// timer) rather than stacking duplicates.
export const pushToast = (stack, toast, max = MAX_TOASTS) =>
  [toast, ...stack.filter((t) => !(t.message === toast.message && t.type === toast.type))].slice(0, max);

let nextId = 0;

// Toast state for a page.
//
//   const { toastProps, showToast } = useToast();
//   showToast('Picked 2 x Oak 6x4', 'success');
//   showToast('Barcode not on this order', 'error');      // stays until dismissed
//   showToast('Saved', 'info', { duration: 2000 });       // duration: null = sticky
//   ...
//   <Toast {...toastProps} />
//
// Every call gets a fresh id, so repeating the same message re-animates it and
// restarts its timer. Up to three toasts stack, newest on top.
const useToast = () => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', options = {}) => {
    if (!message) return null;
    nextId += 1;
    const toast = {
      id: options.id ?? `toast-${nextId}`,
      message,
      type,
      duration: toastDuration(type, options.duration),
    };
    setToasts((stack) => pushToast(stack, toast));
    return toast.id;
  }, []);

  // Dismiss one toast by id, or all of them when called without one
  const dismissToast = useCallback((id) => {
    setToasts((stack) => (id === undefined ? [] : stack.filter((t) => t.id !== id)));
  }, []);

  const toastProps = useMemo(() => ({ toasts, onDismiss: dismissToast }), [toasts, dismissToast]);

  return {
    toast: toasts[0] || null,
    toasts,
    showToast,
    dismissToast,
    toastProps,
  };
};

export default useToast;
